import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { EmailFormField } from "Components/Shared/FormComponents/PersonalDetailsFormTextFields/EmailFormField/EmailFormField";
import { OperatingManagerFormFields } from "Shared/types/OperatingManager";
import { FieldsGroup } from "Components/Shared/FormComponents/FieldsGroup/FieldsGroup";
import { useGetEmployerDetailsQuery } from "./useGetEmployerDetailsQuery";
import { DPEmployerDetails } from "Shared/types/Employer";
import { permissionLabels } from "Shared/types/Admins";

const mapEmployerToFormFields = (employer: DPEmployerDetails) => {
  return {
    firstName: employer.firstName,
    lastName: employer.lastName,
    email: employer.email,
    phoneNumber: employer.workPhoneNumber,
    locations: employer.associatedLocations.items.map(location => ({
      value: location.id,
      label: location.name,
    })),
    permissions: {
      value: employer.permissions,
      label: permissionLabels[employer.permissions],
    },
  };
};

export const useEnterEmailStep = (email: string, goToNextStep: () => void) => {
  const formMethods = useForm<OperatingManagerFormFields>({
    mode: "onBlur",
  });
  const { register, errors, getValues, trigger, setError } = formMethods;
  const { getEmployerDetails } = useGetEmployerDetailsQuery();
  const onComplete = async () => {
    const isValid = await trigger("email");
    if (isValid) {
      const { email } = getValues();
      const employer = await getEmployerDetails(email);
      if (employer) {
        if (employer.employerRewardBudget.id) {
          setError("email", { message: "Operating manager already exists" });
          return false;
        }
        return {
          existingEmployerId: employer.id,
          formFields: mapEmployerToFormFields(employer),
        };
      }
      return {
        existingEmployerId: null,
        formFields: {
          email,
        },
      };
    }

    return false;
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      goToNextStep();
    }
  };

  const StepContent = (
    <FormProvider {...formMethods}>
      <FieldsGroup>
        <EmailFormField
          register={register}
          errors={errors}
          defaultValue={email}
          onKeyPress={handleKeyPress}
        />
      </FieldsGroup>
    </FormProvider>
  );

  return {
    label: "Email",
    content: StepContent,
    onComplete,
  };
};
